import { ExternalLink, Landmark } from 'lucide-react';
import { tx } from '../i18n';
import { formatNumber, formatPence } from '../lib/format';
import type { CouncilTax } from '../types/api';
import ReportSection from './report/ReportSection';

interface CouncilTaxSectionProps {
  councilTax: CouncilTax;
  /** When the report read its sources, for the line at the foot of the section. */
  checkedAt: string;
}

/**
 * What council tax costs here, band by band.
 *
 * <p>The band of this particular home is not published as open data, so the table shows every
 * band, marks the one most homes nearby are in, and sends the reader to the VOA to check.
 */
export default function CouncilTaxSection({ councilTax, checkedAt }: CouncilTaxSectionProps) {
  const { bands, mix, commonBand } = councilTax;
  if (bands.length === 0) {
    return null;
  }
  const shares = new Map(mix.map((entry) => [entry.band, entry]));

  // Written once and used twice: as the column heading, and as the label each cell carries when
  // the table becomes a list of cards on a phone.
  const bandLabel = tx('Band', 'Band', 'Przedział', 'Escalão', 'Tramo');
  const yearLabel = tx('A year', 'Yıllık', 'Rocznie', 'Por ano', 'Al año');
  const monthLabel = tx('A month', 'Aylık', 'Miesięcznie', 'Por mês', 'Al mes');
  const nearbyLabel = tx(
    'Homes nearby',
    'Çevredeki konutlar',
    'Mieszkania w okolicy',
    'Casas nas imediações',
    'Viviendas cercanas',
  );

  return (
    <ReportSection
      id="section-council-tax"
      icon={Landmark}
      title={tx('Council tax', 'Council tax',
    'Podatek lokalny',
    'Imposto municipal',
    'Impuesto municipal')}
      note={tx(
        `${councilTax.authority}, ${councilTax.financialYear}.`,
        `${councilTax.authority}, ${councilTax.financialYear}.`,
        `${councilTax.authority}, ${councilTax.financialYear}.`,
        `${councilTax.authority}, ${councilTax.financialYear}.`,
        `${councilTax.authority}, ${councilTax.financialYear}.`,
      )}
      informational
      quality="estimated"
      source={{
        name: tx(
          'MHCLG council tax levels and VOA stock of properties',
          'MHCLG council tax tutarları ve VOA konut band istatistiği',
          'Stawki podatku lokalnego MHCLG i statystyka zasobu mieszkaniowego VOA',
          'Taxas de imposto municipal do MHCLG e parque habitacional da VOA',
          'Tipos del impuesto municipal del MHCLG y parque de viviendas de la VOA',
        ),
        version: councilTax.financialYear,
        checked: checkedAt,
        href: councilTax.checkUrl,
      }}
    >

      {commonBand ? (
        <p className="small">
          {tx(
            `Most homes around here are in band ${commonBand}: ${formatPence(
              bands.find((band) => band.band === commonBand)?.monthlyPence ?? 0,
            )} a month.`,
            `Buradaki konutların çoğu ${commonBand} bandında: ayda ${formatPence(
              bands.find((band) => band.band === commonBand)?.monthlyPence ?? 0,
            )}.`,
            `Większość mieszkań w okolicy jest w przedziale ${commonBand}: ${formatPence(
              bands.find((band) => band.band === commonBand)?.monthlyPence ?? 0,
            )} miesięcznie.`,
            `A maioria das casas por aqui está no escalão ${commonBand}: ${formatPence(
              bands.find((band) => band.band === commonBand)?.monthlyPence ?? 0,
            )} por mês.`,
            `La mayoría de las viviendas de por aquí están en el tramo ${commonBand}: ${formatPence(
              bands.find((band) => band.band === commonBand)?.monthlyPence ?? 0,
            )} al mes.`,
          )}
        </p>
      ) : null}

      <div className="table-wrap">
        <table className="ct-table">
          <thead>
            <tr>
              <th scope="col">{bandLabel}</th>
              <th scope="col">{yearLabel}</th>
              <th scope="col">{monthLabel}</th>
              {mix.length > 0 ? <th scope="col">{nearbyLabel}</th> : null}
            </tr>
          </thead>
          <tbody>
            {bands.map((band) => {
              const share = shares.get(band.band);
              const common = band.band === commonBand;
              return (
                <tr key={band.band} className={common ? 'ct-table__row ct-table__row--common' : 'ct-table__row'}>
                  <th scope="row" data-label={bandLabel}>
                    {band.band}
                    {common ? (
                      <span className="badge badge--neutral ct-table__tag">
                        {tx('most common', 'en yaygın',
    'najczęstszy',
    'mais comum',
    'más común')}
                      </span>
                    ) : null}
                  </th>
                  <td data-label={yearLabel}>{formatPence(band.annualPence)}</td>
                  <td data-label={monthLabel}>{formatPence(band.monthlyPence)}</td>
                  {mix.length > 0 ? (
                    <td data-label={nearbyLabel}>
                      {share && share.dwellings > 0 ? (
                        <span className="ct-table__share">
                          <span className="meter ct-table__meter" aria-hidden="true">
                            <span style={{ width: `${share.percent}%`, background: 'var(--brand-500)' }} />
                          </span>
                          <span className="tiny muted">
                            {tx(`${share.percent}%`, `%${share.percent}`,
    `${share.percent}%`,
    `${share.percent}%`,
    `${share.percent}%`)}
                          </span>
                        </span>
                      ) : (
                        <span className="tiny muted">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {councilTax.dwellings > 0 ? (
        <p className="tiny muted">
          {tx(
            `Band mix of the ${formatNumber(councilTax.dwellings)} homes in this neighbourhood (LSOA).`,
            `Bu mahalledeki (LSOA) ${formatNumber(councilTax.dwellings)} konutun band dağılımı.`,
    `Rozkład przedziałów wśród ${formatNumber(councilTax.dwellings)} mieszkań w tej okolicy (LSOA).`,
    `Distribuição por escalão das ${formatNumber(councilTax.dwellings)} casas desta zona (LSOA).`,
    `Reparto por tramos de las ${formatNumber(councilTax.dwellings)} viviendas de este barrio (LSOA).`,
          )}
        </p>
      ) : null}

      <p className="tiny muted">{councilTax.note}</p>
      <p className="tiny">
        <a href={councilTax.checkUrl} target="_blank" rel="noreferrer">
          {tx("Check this home's band with the VOA", 'Bu konutun bandını VOA’da kontrol edin',
    'Sprawdź przedział tego mieszkania w VOA',
    'Verifique o escalão desta casa na VOA',
    'Comprueba el tramo de esta vivienda en la VOA')}{' '}
          <ExternalLink size={11} aria-hidden="true" />
        </a>
      </p>
    </ReportSection>
  );
}
